import { useMemo, useState } from "react";
import { Card, Empty, Input, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { displayMessage } from "../../helpers/messages";
import { SL_Select } from "../selects/Select";

/**
 * Etapa 1 — Agrupadores por combinação de respostas do formulário.
 *
 * Renderiza um card para cada combinação única encontrada nos `items`, com:
 *  - rótulo da combinação (gender/frequência/dor/duração p/ treino; gender/tipo/refeições/intolerância p/ dieta)
 *  - tags das sequências cadastradas (ex.: 1, 2, 3)
 *  - tags vermelhas de buracos (ex.: "Falta seq 2")
 *  - contagem de itens
 *
 * Filtros:
 *  - busca textual pelo rótulo da combinação
 *  - selects por dimensão (configuráveis via `filterConfigs`)
 *
 * Props:
 *  - items: array de treinos/dietas (mesmo formato da listagem).
 *  - buildCombination(item): retorna { key: string, label: string }.
 *  - getSequence(item): inteiro da sequência (default item.sequence ?? 1).
 *  - filterConfigs: [{ field, label, options: [{value,label}] }] — dimensões filtráveis.
 *      Filtra os itens-base antes de agrupar, então só aparecem combinações
 *      cujos itens batem com os filtros selecionados.
 *  - itemNamePlural: "treinos" | "dietas" — texto da contagem no card.
 *  - onSelect(group): chamado ao clicar num card. `group` contém
 *      { key, label, seqs:number[], missing:number[], items:object[] }.
 *
 * Todo o agrupamento é feito em memória a partir da lista já carregada —
 * sem chamadas extra ao back, mantendo a tela leve.
 */
export function CombinationsStage({
    items,
    buildCombination,
    getSequence = (i) => i.sequence ?? 1,
    filterConfigs = [],
    itemNamePlural = "itens",
    onSelect,
}) {
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState({});

    function setFilterValue(field, value) {
        setFilters((prev) => {
            const next = { ...prev };
            if (value === undefined || value === null || value === "") {
                delete next[field];
            } else {
                next[field] = value;
            }
            return next;
        });
    }

    const { groups } = useMemo(() => {
        const filterEntries = Object.entries(filters);

        // Aplica os filtros por dimensão sobre os itens antes de agrupar,
        // assim uma combinação só aparece se todos os seus itens couberem
        // nos filtros selecionados.
        const filteredItems = (items || []).filter((item) => {
            return filterEntries.every(([field, value]) => item[field] === value);
        });

        const map = new Map();
        for (const item of filteredItems) {
            const { key, label } = buildCombination(item);
            const seq = Number(getSequence(item)) || 1;
            if (!map.has(key)) {
                map.set(key, { key, label, sequences: new Map(), items: [] });
            }
            const g = map.get(key);
            // conta quantos itens existem por sequência (split pode ter vários)
            g.sequences.set(seq, (g.sequences.get(seq) || 0) + 1);
            g.items.push(item);
        }

        const groupsArr = Array.from(map.values()).map((g) => {
            const seqs = Array.from(g.sequences.keys()).sort((a, b) => a - b);
            const maxSeq = seqs.length ? seqs[seqs.length - 1] : 0;
            // buracos: inteiros em 1..maxSeq que não existem
            const missing = [];
            for (let s = 1; s <= maxSeq; s++) {
                if (!g.sequences.has(s)) missing.push(s);
            }
            return { ...g, seqs, missing };
        });

        groupsArr.sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
        return { groups: groupsArr };
    }, [items, filters, buildCombination, getSequence]);

    // busca textual sobre o rótulo (após filtros por dimensão)
    const visibleGroups = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return groups;
        return groups.filter((g) => g.label.toLowerCase().includes(q));
    }, [groups, search]);

    return (
        <div className="flex flex-col flex-1 min-h-0 overflow-auto p-2">
            {/* Barra de filtros */}
            <div className="flex flex-wrap items-center gap-2 mb-3 bg-white p-2 rounded border border-gray-200">
                <div className="flex items-center gap-2 text-blue-700 font-semibold text-sm whitespace-nowrap">
                    {displayMessage("COMBINATIONS_FILTER")}:
                </div>
                {filterConfigs.map((cfg) => (
                    <SL_Select
                        key={cfg.field}
                        placeholder={cfg.label}
                        allowClear
                        style={{ minWidth: 160 }}
                        value={filters[cfg.field]}
                        options={[
                            { value: "", label: displayMessage("COMBINATIONS_ALL") },
                            ...cfg.options,
                        ]}
                        onChange={(value) => setFilterValue(cfg.field, value)}
                    />
                ))}
                <div className="flex-1 min-w-[180px]">
                    <Input
                        allowClear
                        prefix={<SearchOutlined className="text-gray-400" />}
                        placeholder={displayMessage("COMBINATIONS_SEARCH")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* Cabeçalho */}
            <div className="flex flex-col mb-2">
                <div className="text-blue-700 font-semibold text-base">
                    {displayMessage("COMBINATIONS_TITLE")}
                </div>
                <div className="text-gray-500 text-sm">
                    {displayMessage("COMBINATIONS_SUBTITLE")}
                </div>
            </div>

            {/* Grid de cards */}
            {visibleGroups.length === 0 ? (
                <div className="p-4 bg-white rounded border border-gray-200">
                    <Empty description={displayMessage("COMBINATIONS_EMPTY")} />
                </div>
            ) : (
                <div className="grid gap-3 grid-cols-1 ss:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
                    {visibleGroups.map((g) => (
                        <Card
                            key={g.key}
                            hoverable
                            size="small"
                            className="cursor-pointer border-gray-200 hover:border-blue-500 transition"
                            onClick={() => onSelect(g)}
                        >
                            <div className="flex flex-col gap-2">
                                <div className="text-blue-700 font-semibold text-sm leading-snug">
                                    {g.label}
                                </div>

                                <div className="flex flex-wrap gap-1">
                                    {g.seqs.map((s) => (
                                        <Tag key={s} color="green" className="m-0">
                                            {displayMessage("SEQUENCE")} {s}
                                        </Tag>
                                    ))}
                                    {g.missing.map((s) => (
                                        <Tag key={`hole-${s}`} color="error" className="m-0">
                                            {displayMessage("COMBINATIONS_HOLE_PREFIX")} {s}
                                        </Tag>
                                    ))}
                                </div>

                                <div className="text-gray-500 text-xs">
                                    {g.items.length} {itemNamePlural}
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
